/**
 * Derives a per-business "brand accent" color so a business page reads as
 * that business's own space rather than a fixed platform purple everywhere.
 *
 * Strategy (Phase 1 — automatic, zero owner effort):
 *  1. If the business has a logo, sample it client-side (canvas) for its
 *     dominant *vibrant* hue, then clamp saturation/lightness into a safe
 *     band so the result stays legible as text and as a banner backdrop.
 *  2. If there's no logo, or the image can't be read (CORS/network), fall
 *     back to a fixed palette keyed by listingType — every category still
 *     gets a distinct, deliberate color instead of the same purple.
 *  3. Results are cached by image URL for the life of the tab — extraction
 *     only ever runs once per logo.
 */

// ─── Category fallback palette ──────────────────────────────────────────────
// Applies when there's no logo yet (or extraction fails) — keeps every
// listing type visually distinct without requiring any owner input.
const CATEGORY_ACCENTS: Record<string, string> = {
  new_product: "#0d9488", // teal — fresh/new
  used_product: "#b45309", // amber — reuse/vintage
  menu: "#c2410c", // terracotta — food & dining
  rental: "#4338ca", // indigo — rentals
  real_estate: "#1d4ed8", // blue — real estate
  others: "#6a11cb", // platform default — closest to the old universal purple
};

const DEFAULT_ACCENT = "#6a11cb";

export function getCategoryAccent(listingType?: string | null): string {
  if (!listingType) return DEFAULT_ACCENT;
  return CATEGORY_ACCENTS[listingType] ?? DEFAULT_ACCENT;
}

// ─── Color space helpers ────────────────────────────────────────────────────

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

export function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Darkens a hex color by reducing HSL lightness — used for gradient pairs. */
export function darkenHex(hex: string, amount = 14): string {
  const [r, g, b] = hexToRgb(hex);
  const [h, s, l] = rgbToHsl(r, g, b);
  return hslToHex(h, s, Math.max(10, l - amount));
}

/** A soft pastel of the given colour — same hue, lightness fixed high and
 *  saturation capped — used as the poster/card background so generated assets
 *  look light and airy rather than inheriting the (dark) page accent. */
export function pastelHex(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  const [h, s] = rgbToHsl(r, g, b);
  return hslToHex(h, Math.min(65, s), 88);
}

/** Clamps a raw sampled hue into a safe, legible accent — saturated enough to
 *  read as "branded", dark enough to stay legible as text on white and to
 *  carry white text/icons on top of it. */
function clampForAccent(h: number, s: number): string {
  const safeS = Math.min(78, Math.max(48, s));
  const safeL = 36; // fixed mid-dark lightness — the sweet spot for both uses above
  return hslToHex(h, safeS, safeL);
}

// ─── Dominant color extraction ──────────────────────────────────────────────

const extractionCache = new Map<string, Promise<string | null>>();

/**
 * Samples an image for its dominant *vibrant* color (biased toward saturated,
 * non-neutral pixels so a mostly-white logo with a colored mark still yields
 * that mark's color rather than gray). Resolves null on any failure (CORS,
 * network, decode) so callers can fall back to the category palette.
 */
export function extractDominantColor(imageUrl: string): Promise<string | null> {
  if (!imageUrl) return Promise.resolve(null);
  const cached = extractionCache.get(imageUrl);
  if (cached) return cached;

  const promise = new Promise<string | null>((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const size = 24;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, size, size);
          const { data } = ctx.getImageData(0, 0, size, size);

          let sumX = 0;
          let sumY = 0;
          let sumS = 0;
          let weight = 0;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            if (a < 128) continue;
            const [h, s, l] = rgbToHsl(r, g, b);
            // Skip near-white, near-black, and low-saturation (gray) pixels —
            // these are backgrounds/outlines, not the brand mark itself.
            if (l > 92 || l < 8 || s < 15) continue;
            // Weight vibrant pixels more heavily so a small colored logomark
            // wins over a larger muted background area.
            const w = s;
            const rad = (h * Math.PI) / 180;
            sumX += Math.cos(rad) * w;
            sumY += Math.sin(rad) * w;
            sumS += s * w;
            weight += w;
          }

          if (weight === 0) return resolve(null);
          const avgHue = (Math.atan2(sumY, sumX) * 180) / Math.PI;
          const hue = avgHue < 0 ? avgHue + 360 : avgHue;
          const avgSat = sumS / weight;
          resolve(clampForAccent(hue, avgSat));
        } catch {
          // getImageData throws if the canvas got tainted by a non-CORS image
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = imageUrl;
    } catch {
      resolve(null);
    }
  });

  extractionCache.set(imageUrl, promise);
  return promise;
}
